/**
 * How sure we are — what the record shows.
 *
 * Reads an item's knowledge status and confidence off what is already recorded
 * about it, instead of asking a person to type them: the evidence linked to it
 * and how reliable each piece is, how many people contributed values and
 * whether they disagree, where its effort number came from, and whether an
 * agent created it without a person's review. Nothing here is stored; it is
 * computed at read time, the way readiness is (canon 04) and the way the
 * planning Monte Carlo reads uncertainty off recorded signals.
 *
 * Why: the typed fields (`items.knowledge_status`, `items.confidence_level`,
 * migration 413) sat at their defaults on 792 of 794 production items on
 * 2026-10-15. A field a person must remember to set stays unset. The reading
 * is the version that can be true. Research note:
 * docs/9-exploration/RESEARCH-what-reality-has-that-the-software-does-not.md.
 *
 * Two words, never one: knowledge status says what kind of basis the facts
 * have; confidence says how well that basis supports them. `null` confidence
 * means "not assessed" — nothing on the record — which is a different thing
 * from "low".
 *
 * Rule-based on purpose (like the item standard). The first AI step —
 * reading discussion and description — comes later, and proposes rather than
 * writes (canon 22 §5).
 */

import type { AcceptedValue, ValueProposal, ItemConfidenceLevel, ItemKnowledgeStatus } from '../types/prioritization/itemLifecycle';
import { CONFIDENCE_LEVEL_LABELS, KNOWLEDGE_STATUS_LABELS } from '../types/prioritization/itemLifecycle';
import type { Evidence } from '../types/prioritization/evidence';
import { isAgentProposer } from './valueStatus';

export type CertaintyGap = 'evidence' | 'values' | 'effort';

export interface CertaintyLine {
  id: string;
  /** One plain sentence. */
  text: string;
  /** Present when the line is a gap that one action can close. */
  gap?: CertaintyGap;
}

export interface ItemCertaintyReading {
  /** What kind of basis the item's facts have, read off the record. */
  knowledge: ItemKnowledgeStatus;
  /** How well the record supports them; null = nothing on the record to assess. */
  confidence: ItemConfidenceLevel | null;
  /** "Known · High confidence", "Assumed · Low confidence", "Unknown · Not assessed". */
  headline: string;
  /** One sentence saying why. */
  summary: string;
  /** Every line of the basis, gaps included, in a fixed order. */
  lines: CertaintyLine[];
  /** The lines that are gaps, each closable by one action. */
  gaps: CertaintyLine[];
  /** Where a typed field claims more than the record shows; each null when it does not. */
  typedGaps: { knowledge: string | null; confidence: string | null };
}

export interface ItemCertaintyInput {
  item: {
    knowledge_status?: ItemKnowledgeStatus | null;
    confidence_level?: ItemConfidenceLevel | null;
    effortCapacityUnits?: number | null;
    effortSource?: 'explicit' | 'planner_override' | 'work_size' | 'altitude_level_inferred' | null;
    creationSource?: string | null;
    creationReviewStatus?: string | null;
    updatedAt?: Date | string | null;
  };
  /** Evidence attached to this item. */
  evidence: Evidence[];
  /** The item's official (not session-scoped) accepted values. */
  acceptedValues: AcceptedValue[];
  /** Every value proposal on this item. */
  proposals: ValueProposal[];
  /** The criteria the item can carry a value on (its pools' criteria and the workspace's attributes). */
  criteriaKeys: string[];
  /** Words for a criterion key, for the sentence that names a contested one. */
  attributeLabel?: (key: string) => string;
  now?: Date;
}

const STRONG = new Set(['verified', 'corroborated']);
const WEAK = new Set(['single_source', 'anecdotal']);
const PROPOSAL_NOT_COUNTED = new Set(['withdrawn', 'rejected']);
const STALE_AFTER_DAYS = 90;

/** Order used only to say whether a typed field claims more than the record shows. */
const KNOWLEDGE_STRENGTH: Record<ItemKnowledgeStatus, number> = {
  known: 4,
  estimated: 3,
  assumed: 2,
  inferred: 2,
  disputed: 1,
  unknown: 0,
};
const CONFIDENCE_STRENGTH: Record<ItemConfidenceLevel, number> = { low: 1, medium: 2, high: 3 };

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);
const people = (n: number) => (n === 1 ? 'one person' : `${n} people`);
const lower = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

function isLive(e: Evidence, now: Date): boolean {
  if (e.isExpired) return false;
  if (e.expiresAt && new Date(e.expiresAt).getTime() < now.getTime()) return false;
  return true;
}

export function deriveItemCertainty(input: ItemCertaintyInput): ItemCertaintyReading {
  const now = input.now ?? new Date();
  const label = input.attributeLabel ?? ((k: string) => k);

  // ── Evidence ──
  const live = input.evidence.filter((e) => isLive(e, now));
  const expired = input.evidence.length - live.length;
  const strong = live.filter((e) => STRONG.has(e.reliability)).length;
  const weak = live.filter((e) => WEAK.has(e.reliability)).length;
  const speculative = live.filter((e) => e.reliability === 'speculative').length;

  // ── Values ──
  const counted = input.proposals.filter((p) => !PROPOSAL_NOT_COUNTED.has(String(p.status)));
  const contributors = new Set(counted.filter((p) => !isAgentProposer(p.proposedBy)).map((p) => p.proposedBy));
  for (const v of input.acceptedValues) if (v.acceptedBy && !isAgentProposer(v.acceptedBy)) contributors.add(v.acceptedBy);
  const agentValues = counted.filter((p) => isAgentProposer(p.proposedBy)).length;
  const valuesOn = new Set(input.acceptedValues.map((v) => v.attributeKey));
  const criteria = new Set(input.criteriaKeys);
  for (const k of valuesOn) criteria.add(k);
  const contestedKeys = [...new Set(input.acceptedValues.filter((v) => v.isContested).map((v) => v.attributeKey))];

  // ── Effort ──
  const effortSet = input.item.effortCapacityUnits != null;
  const effortInferred = effortSet && input.item.effortSource === 'altitude_level_inferred';

  // ── Origin ──
  const agentMade = input.item.creationSource === 'ai' || input.item.creationSource === 'automated';
  const unreviewed = agentMade && input.item.creationReviewStatus !== 'human_reviewed';

  // ── Age ──
  const updated = input.item.updatedAt ? new Date(input.item.updatedAt) : null;
  const ageDays = updated && !Number.isNaN(updated.getTime()) ? Math.floor((now.getTime() - updated.getTime()) / 86_400_000) : null;

  const anything = live.length > 0 || valuesOn.size > 0 || counted.length > 0 || effortSet;

  // ── Knowledge status ──
  let knowledge: ItemKnowledgeStatus;
  if (!anything) knowledge = 'unknown';
  else if (contestedKeys.length > 0) knowledge = 'disputed';
  else if (strong > 0) knowledge = 'known';
  else if (weak > 0 || contributors.size >= 2) knowledge = 'estimated';
  else if (contributors.size === 0 && (agentValues > 0 || effortInferred || unreviewed)) knowledge = 'inferred';
  else knowledge = 'assumed';

  // ── Confidence ──
  let confidence: ItemConfidenceLevel | null;
  if (!anything) confidence = null;
  else if (contestedKeys.length > 0 || unreviewed) confidence = 'low';
  else if (strong >= 2 || (strong >= 1 && contributors.size >= 2)) confidence = 'high';
  else if (strong >= 1 || weak >= 2 || contributors.size >= 3) confidence = 'medium';
  else confidence = 'low';

  // ── Lines ──
  const lines: CertaintyLine[] = [];

  if (input.evidence.length === 0) {
    lines.push({ id: 'evidence', text: 'No evidence linked.', gap: 'evidence' });
  } else {
    const parts: string[] = [];
    if (strong) parts.push(`${strong} verified or corroborated`);
    if (weak) parts.push(`${weak} from one source or anecdotal`);
    if (speculative) parts.push(`${speculative} speculative`);
    if (expired) parts.push(`${expired} expired`);
    const n = input.evidence.length;
    const text = live.length === 0
      ? `${n} ${plural(n, 'piece', 'pieces')} of evidence, all expired.`
      : `${n} ${plural(n, 'piece', 'pieces')} of evidence: ${parts.join(', ')}.`;
    lines.push({ id: 'evidence', text, gap: live.length === 0 ? 'evidence' : undefined });
  }

  if (valuesOn.size === 0 && counted.length > 0) {
    // Proposed but not accepted: the record has a person's number, not a value anyone stands behind yet.
    const who: string[] = [];
    if (contributors.size) who.push(people(contributors.size));
    if (agentValues) who.push('an agent');
    lines.push({
      id: 'values',
      text: `${counted.length} ${plural(counted.length, 'value', 'values')} proposed by ${who.join(' and ')}, none accepted yet.`,
      gap: 'values',
    });
  } else if (valuesOn.size === 0) {
    lines.push({
      id: 'values',
      text: criteria.size === 0 ? 'No values yet.' : `No values on the ${criteria.size} ${plural(criteria.size, 'criterion', 'criteria')} yet.`,
      gap: 'values',
    });
  } else {
    const who: string[] = [];
    if (contributors.size) who.push(`from ${people(contributors.size)}`);
    if (agentValues) who.push(`${agentValues} from an agent`);
    let text = `Values on ${valuesOn.size} of ${criteria.size} ${plural(criteria.size, 'criterion', 'criteria')}${who.length ? `, ${who.join(', ')}` : ''}.`;
    if (contestedKeys.length) {
      const names = contestedKeys.map(label);
      text += ` ${names.join(', ')} ${plural(names.length, 'is', 'are')} contested.`;
    }
    lines.push({ id: 'values', text });
  }

  if (!effortSet) lines.push({ id: 'effort', text: 'No effort estimate.', gap: 'effort' });
  else if (effortInferred) lines.push({ id: 'effort', text: 'Effort inferred from altitude, not estimated.', gap: 'effort' });
  else if (input.item.effortSource === 'planner_override') lines.push({ id: 'effort', text: 'Effort set by the planner.' });
  else if (input.item.effortSource === 'work_size') lines.push({ id: 'effort', text: 'Effort set from work size.' });
  else lines.push({ id: 'effort', text: 'Effort estimated directly.' });

  if (agentMade) {
    lines.push({ id: 'origin', text: unreviewed ? 'Created by an agent; not yet reviewed by a person.' : 'Created by an agent; reviewed by a person.' });
  }

  if (ageDays != null && ageDays >= STALE_AFTER_DAYS) {
    lines.push({ id: 'age', text: `Nothing has changed in ${ageDays} days.` });
  }

  // ── Summary ──
  let summary: string;
  switch (knowledge) {
    case 'unknown':
      summary = 'Nothing on the record yet.';
      break;
    case 'disputed':
      summary = contestedKeys.length === 1 ? `People disagree on ${label(contestedKeys[0])}.` : 'People disagree on more than one value.';
      break;
    case 'known':
      summary = strong >= 2 ? 'Backed by more than one piece of verified evidence.' : 'Backed by verified evidence.';
      break;
    case 'estimated':
      summary = weak > 0 ? 'Some evidence, none of it verified.' : 'Several people agree; no evidence linked.';
      break;
    case 'inferred':
      summary = unreviewed ? 'Created by an agent and not yet reviewed.' : 'Only inferred numbers; nobody has estimated it.';
      break;
    default:
      summary = "One person's say-so, no evidence.";
  }

  const headline = `${KNOWLEDGE_STATUS_LABELS[knowledge]} · ${confidence ? CONFIDENCE_LEVEL_LABELS[confidence] : 'Not assessed'}`;

  // ── Typed fields claiming more than the record shows ──
  const typedK = input.item.knowledge_status ?? null;
  const knowledgeGap =
    typedK && typedK !== 'unknown' && KNOWLEDGE_STRENGTH[typedK] > KNOWLEDGE_STRENGTH[knowledge]
      ? `Marked ${KNOWLEDGE_STATUS_LABELS[typedK]}; the record shows ${lower(KNOWLEDGE_STATUS_LABELS[knowledge])}: ${lower(summary)}`
      : null;
  const typedC = input.item.confidence_level ?? null;
  const confidenceGap =
    typedC && CONFIDENCE_STRENGTH[typedC] > (confidence ? CONFIDENCE_STRENGTH[confidence] : 0)
      ? `Marked ${lower(CONFIDENCE_LEVEL_LABELS[typedC])}; the record supports ${confidence ? lower(CONFIDENCE_LEVEL_LABELS[confidence]) : 'none yet'}.`
      : null;

  return {
    knowledge,
    confidence,
    headline,
    summary,
    lines,
    gaps: lines.filter((l) => l.gap),
    typedGaps: { knowledge: knowledgeGap, confidence: confidenceGap },
  };
}
